import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  Placement,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useId,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import React, {
  cloneElement,
  isValidElement,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import { Button, ButtonProps } from '@/components/Button';
import { cn } from '@/utils/classname';

interface PopoverProps extends ButtonProps, PropsWithChildren {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  placement?: Placement;
  container?: ReactNode;
  className?: string;
  matchContainerWidth?: boolean;
  interactions?: 'hover' | 'click';
  allowFlip?: boolean;
}

export const Popover = (props: PopoverProps) => {
  const {
    open: openParent,
    setOpen: setOpenParent,
    placement = 'bottom-start',
    children,
    container,
    className,
    matchContainerWidth,
    interactions = 'click',
    allowFlip = true,
    ...rest
  } = props;

  // Standard controlled/uncontrolled pattern - replaces useStorage to fix FloatingUI circular dependency
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openParent !== undefined ? openParent : internalOpen;
  const setOpen = (value: boolean) => {
    setInternalOpen(value);
    setOpenParent?.(value);
  };

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(6),
      ...(allowFlip ? [flip({ fallbackAxisSideDirection: 'end' })] : []),
      shift({ padding: 8 }),
      size({
        padding: 10,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${availableHeight}px`;
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const isHover = interactions === 'hover';
  const isClick = interactions === 'click';

  // Always call hooks unconditionally to follow rules of hooks
  const click = useClick(context, { enabled: isClick });
  const hover = useHover(context, {
    enabled: isHover,
    delay: { open: 250, close: 250 },
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
    ...(isHover ? [hover] : []),
    ...(isClick ? [click] : []),
  ]);

  const headingId = useId();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const childrenRef = (props?.children as any)?.ref;
  const triggerRef = useMergeRefs([refs.setReference, childrenRef]);

  // Store container width in state to avoid accessing refs during render
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined
  );

  // Measure container width after it's mounted or when matchContainerWidth changes
  useEffect(() => {
    if (matchContainerWidth && refs.reference.current) {
      const element = refs.reference.current as HTMLElement;
      setContainerWidth(element.clientWidth || undefined);

      // Update width on resize
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width || undefined);
        }
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    } else {
      setContainerWidth(undefined);
    }
  }, [matchContainerWidth, refs.reference]);

  // Get reference props without ref to avoid ESLint rule violation
  const referenceProps = getReferenceProps();

  return (
    <>
      {container && isValidElement(container) ? (
        // FloatingUI requires refs to be passed to cloneElement for proper positioning
        // This is safe because triggerRef is from useMergeRefs, not direct ref access
        // Container's original props are preserved and override FloatingUI defaults
        cloneElement(
          container as React.ReactElement<Record<string, unknown>>,
          // eslint-disable-next-line react-hooks/refs
          {
            ...referenceProps,
            ...(container.props as Record<string, unknown>),
            ref: triggerRef,
          } as Record<string, unknown>
        )
      ) : (
        <Button ref={triggerRef} {...referenceProps} {...rest} />
      )}
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              className={cn(
                'Popover border-chariot-brand-primary bg-chariot-background-primary text-chariot-text-primary z-[9999] rounded border p-4 shadow-md outline-none',
                className
              )}
              ref={refs.setFloating}
              style={{
                width: containerWidth,
                ...floatingStyles,
              }}
              aria-labelledby={headingId}
              {...getFloatingProps()}
            >
              {children || props.label}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};
